// src/components/bugReport/SuggestionModal.jsx
//
// Modal opened by the lightbulb button in BugReportFAB. Collects a one-line
// idea + optional detail, auto-attaches the current page for context, posts
// to /api/report-bug with report_type: 'suggestion'. Lands in
// feature_suggestions and loads into the next Claude Code session for
// evaluation.

import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import { Loader2, Send } from 'lucide-react'

export default function SuggestionModal({ isOpen, onClose }) {
  const { user, profile } = useAuth()
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!title.trim()) {
      toast.warning('Give your idea a one-line summary first')
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        report_type:      'suggestion',
        reporter_user_id: user?.id || null,
        reporter_email:   user?.email || null,
        reporter_name:    profile?.full_name || profile?.name || user?.email || 'Unknown',
        url:              window.location.href,
        page_title:       document.title,
        title:            title.trim(),
        description:      description.trim(),
      }

      const res = await fetch('/api/report-bug', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      })

      if (!res.ok) {
        const txt = await res.text().catch(() => '')
        throw new Error(txt || `HTTP ${res.status}`)
      }

      toast.success('Suggestion filed — it goes in front of Claude for evaluation in the next build session.', 5000)
      setTitle('')
      setDescription('')
      onClose()
    } catch (err) {
      console.error('[Suggestion] submit failed:', err)
      toast.error(`Couldn't send suggestion: ${err.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Suggestion box" size="lg">
      <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 overflow-y-auto">
        <p className="text-sm text-warm-600">
          Wish Chatrone did something it doesn't? Drop the idea here — every
          suggestion is evaluated for the build queue, and you can follow its
          status in the suggestion box.
        </p>

        <div>
          <label className="block text-xs font-semibold text-warm-700 mb-1.5">
            THE IDEA, IN ONE LINE
          </label>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Let me duplicate a project with all its crew"
            maxLength={200}
            className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-400"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-warm-700 mb-1.5">
            MORE DETAIL <span className="font-normal text-warm-500">(optional — what would it do, and when would you use it?)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. When we spin up a sequel or a new season, the crew list is 90% the same — re-entering everyone takes an hour"
            rows={4}
            className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-400 resize-none"
          />
        </div>

        <div className="text-xs text-warm-500 border-t border-warm-200 pt-3">
          Also sent automatically: the page you're on, so we know which part of
          Chatrone you're talking about.
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-warm-700 hover:bg-warm-100 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !title.trim()}
            className="px-4 py-2 text-sm bg-navy-600 hover:bg-navy-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            {submitting
              ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
              : <><Send size={14} /> Send suggestion</>
            }
          </button>
        </div>
      </form>
    </Modal>
  )
}
